import { useState } from "react";
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Image,
  ListGroup,
  Card,
  Button,
  Form,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  useGetProductDetailsQuery,
  useCreateReviewMutation,
} from "../slices/productsApiSlice";
import Rating from "../components/Rating";
import Loader from "../components/Loader";
import Message from "../components/Message";
import Meta from "../components/Meta";
import { addToCart } from "../slices/cartSlice";
import { useGetDiscountQuery } from "../slices/discountApiSlice";

const ProductScreen = () => {
  const { id: productId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [customComment, setCustomComment] = useState("");

  const addToCartHandler = () => {
    const priceToUse = discountActive ? Number(discountedPrice) : product.price;
    dispatch(addToCart({ ...product, price: priceToUse, qty, customComment }));
    navigate("/cart");
  };

  const {
    data: product,
    isLoading,
    refetch,
    error,
  } = useGetProductDetailsQuery(productId);

  const { userInfo } = useSelector((state) => state.auth);

  const { data: discount } = useGetDiscountQuery();
  const discountActive =
    discount?.isActive &&
    discount?.endTime &&
    new Date() <= new Date(discount.endTime) &&
    discount.percentage > 0;
  const discountedPrice = discountActive && product
    ? Math.round(product.price * (1 - discount.percentage / 100))
    : null;

  const [createReview, { isLoading: loadingProductReview }] =
    useCreateReviewMutation();

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      await createReview({
        productId,
        rating,
        comment,
      }).unwrap();
      refetch();
      toast.success("Review created successfully");
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <div className="text-end">
        <Link className="btn btn-light my-3" to="/">
          חזור
        </Link>
      </div>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || error.error}
        </Message>
      ) : (
        <>
          <Meta title={product.name} description={product.description} />
          <Row>
            <Col md={3} className="d-none d-md-block">
              <Card>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <Row>
                      <Col className="text-end">
                        {discountActive ? (
                          <span>
                            <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: '10px' }}>
                              ₪{product.price}
                            </span>
                            <strong style={{ color: '#6B46C1' }}>₪{discountedPrice}</strong>
                          </span>
                        ) : (
                          <strong>₪{product.price}</strong>
                        )}
                      </Col>
                      <Col className="text-end">:מחיר</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col className="text-end">
                        {product.countInStock > 0 ? "קיים במלאי" : "חסר במלאי"}
                      </Col>
                      <Col className="text-end">:מלאי</Col>
                    </Row>
                  </ListGroup.Item>

                  {/* Qty Select */}
                  {product.countInStock > 0 && (
                    <ListGroup.Item>
                      <Row>
                        <Col className="text-end">
                          <Form.Control
                            as="select"
                            value={qty}
                            onChange={(e) => setQty(Number(e.target.value))}
                            className="text-end"
                          >
                            {/* Will display a combo box in the Qty field, the combo length is according to the countInStock */}
                            {[...Array(product.countInStock).keys()].map(
                              (x) => (
                                <option key={x + 1} value={x + 1}>
                                  {x + 1}
                                </option>
                              )
                            )}
                          </Form.Control>
                        </Col>
                        <Col className="text-end">:כמות</Col>
                      </Row>
                    </ListGroup.Item>
                  )}

                  <ListGroup.Item className="d-flex justify-content-end">
                    <Button
                      className="btn-block"
                      type="button"
                      disabled={product.countInStock === 0}
                      onClick={addToCartHandler}
                    >
                      הוסף לעגלה
                    </Button>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
            <Col md={3} className="order-2 order-md-2">
              <ListGroup variant="flush">
                <ListGroup.Item className="text-end">
                  <h3>{product.name}</h3>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="text-end">
                    <Rating
                      value={product.rating}
                      text={`${product.numReviews} חוות דעת`}
                    />
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="text-end">
                  {discountActive ? (
                    <span>
                      <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: '10px' }}>
                        ₪{product.price}
                      </span>
                      <span style={{ color: '#6B46C1', fontWeight: '700' }}>₪{discountedPrice}</span>
                    </span>
                  ) : (
                    <span>₪{product.price}</span>
                  )}
                  {' '}:מחיר
                </ListGroup.Item>
                <ListGroup.Item className="text-end" dir="rtl" style={{ whiteSpace: 'pre-wrap' }}>
                  <ReactMarkdown>{product.description}</ReactMarkdown>
                </ListGroup.Item>
                  {product.enableCustomComment && (
                    <ListGroup.Item>
                      <Form.Group controlId="customComment">
                        <Col xs={12} className="text-end">
                          <Form.Label>
                            {product.customCommentLabel || 'הערה / בקשה מיוחדת'}
                          </Form.Label>
                        </Col>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          maxLength={product.customCommentMaxLength || 200}
                          value={customComment}
                          onChange={(e) => setCustomComment(e.target.value)}
                          dir="rtl"
                          placeholder={product.customCommentLabel || 'לדוגמה: טבעת אדומה x2, כחולה x1...'}
                        />
                        <div className="text-end" style={{ fontSize: '0.8rem', color: '#888' }}>
                          {customComment.length} / {product.customCommentMaxLength || 200}
                        </div>
                      </Form.Group>
                    </ListGroup.Item>
                  )}
                </ListGroup>
            </Col>
            <Col md={6} className="order-1 order-md-3 d-flex justify-content-end">
              <Image src={product.image} alt={product.name} fluid loading="lazy" style={{ maxHeight: '30rem' }} />
            </Col>
          </Row>
          <Row className="review">
            <Col md={6} className="ms-auto">
              <h2 className="text-end">חוות דעת</h2>
              {product.reviews.length === 0 && (
                <div className="text-end">
                  <Message>אין חוות דעת</Message>
                </div>
              )}
              <ListGroup variant="flush">
                {product.reviews.map((review) => (
                  <ListGroup.Item key={review._id} className="text-end">
                    <strong>{review.name}</strong>
                    <Rating value={review.rating} />
                    <p>{review.createdAt.substring(0, 10)}</p>
                    <p>{review.comment}</p>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <h2 className="text-end">כתוב חוות דעת</h2>

              {loadingProductReview && <Loader />}

              {userInfo ? (
                <Form onSubmit={submitHandler}>
                  <Form.Group className="my-2" controlId="rating">
                    <Col xs={12} className="text-end">
                      <Form.Label>ציון מוצר</Form.Label>
                    </Col>
                    <Form.Control
                      as="select"
                      required
                      value={rating}
                      onChange={(e) => setRating(e.target.value)}
                      dir="rtl"
                    >
                      <option value="">בחר...</option>
                      <option value="1">1 - גרוע</option>
                      <option value="2">2 - טעון שיפור</option>
                      <option value="3">3 - טוב</option>
                      <option value="4">4 - טוב מאוד</option>
                      <option value="5">5 - מצוין</option>
                    </Form.Control>
                  </Form.Group>
                  <Form.Group className="my-2" controlId="comment">
                    <Col xs={12} className="text-end">
                      <Form.Label>חוות דעת</Form.Label>
                    </Col>
                    <Form.Control
                      as="textarea"
                      row="3"
                      required
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      dir="rtl"
                    ></Form.Control>
                  </Form.Group>
                  <Row className="my-2">
                    <Col className="d-flex justify-content-end">
                      <Button
                        disabled={loadingProductReview}
                        type="submit"
                        variant="primary"
                      >
                        הוסף חוות דעת
                      </Button>
                    </Col>
                  </Row>
                </Form>
              ) : (
                <div className="text-end">
                  <Message>
                    <Link to="/login">היכנס</Link> על מנת לכתוב חוות דעת
                  </Message>
                </div>
              )}
            </Col>
          </Row>

          {/* Cart card shown below reviews on mobile only */}
          <Row className="d-md-none mt-3">
            <Col>
              <Card>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <Row>
                      <Col className="text-end">
                        {discountActive ? (
                          <span>
                            <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: '10px' }}>
                              ₪{product.price}
                            </span>
                            <strong style={{ color: '#6B46C1' }}>₪{discountedPrice}</strong>
                          </span>
                        ) : (
                          <strong>₪{product.price}</strong>
                        )}
                      </Col>
                      <Col className="text-end">:מחיר</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col className="text-end">
                        {product.countInStock > 0 ? "קיים במלאי" : "חסר במלאי"}
                      </Col>
                      <Col className="text-end">:מלאי</Col>
                    </Row>
                  </ListGroup.Item>
                  {product.countInStock > 0 && (
                    <ListGroup.Item>
                      <Row>
                        <Col className="text-end">
                          <Form.Control
                            as="select"
                            value={qty}
                            onChange={(e) => setQty(Number(e.target.value))}
                            className="text-end"
                          >
                            {[...Array(product.countInStock).keys()].map((x) => (
                              <option key={x + 1} value={x + 1}>
                                {x + 1}
                              </option>
                            ))}
                          </Form.Control>
                        </Col>
                        <Col className="text-end">:כמות</Col>
                      </Row>
                    </ListGroup.Item>
                  )}
                  <ListGroup.Item className="d-flex justify-content-end">
                    <Button
                      className="btn-block"
                      type="button"
                      disabled={product.countInStock === 0}
                      onClick={addToCartHandler}
                    >
                      הוסף לעגלה
                    </Button>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};
export default ProductScreen;
